import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/App";
import { PageHeader, RoleBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, KeyRound } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/profile", { name });
      await refresh();
      toast.success("Profile updated");
    } catch {
      toast.error("Update failed");
    } finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.put("/auth/password", { current_password: cur || null, new_password: next });
      toast.success("Password set — you can now sign in with email + password");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Password change failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <PageHeader title="My Profile" sub="Your account details and sign-in security" />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-600 font-bold text-lg">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <p className="font-semibold text-slate-900">{user?.email}</p>
            <RoleBadge role={user?.role} />
          </div>
        </div>
        <form onSubmit={saveProfile} className="flex items-end gap-3">
          <div className="space-y-1.5 flex-1">
            <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Display Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
          </div>
          <Button type="submit" disabled={busy} data-testid="profile-save-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-6 transition-colors duration-200">
            <UserCircle className="w-4 h-4 mr-1.5" /> Save
          </Button>
        </form>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-emerald-600" />
          <h3 className="font-semibold text-slate-900">Password Login</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5">Set or change your password to sign in with email + password (Google & OTP keep working).</p>
        <form onSubmit={savePassword} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Current Password (only if already set)</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="password-current-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">New Password</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="password-new-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Confirm New Password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="password-confirm-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="password-save-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-6 transition-colors duration-200">
            {busy ? "Updating…" : "Update Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
